import { Origin } from "../../types/general-types";
import { InstitutionAccountLink } from "../../types/institution.type";
import { SplitCategory } from "@bitwarden/web-vault/app/models/data/shared/split-category/split-category";
import { SplitCategoryFactory } from "@bitwarden/web-vault/app/models/data/shared/split-category/split-category.factory";
import { SplitClassificationType } from "../../types/split-classification-type";

import { Book } from "../../data/blobby/book.data";
import { ModelView } from "@bitwarden/web-vault/app/models/view/model.view";
import {
  AccountModelVersion,
  AccountStoreModel,
  DefaultAccountStoreModel,
} from "@bitwarden/web-vault/app/models/store/account.store.model";

import {
  BalanceCalculatedProperties,
  CalculatedProperty,
} from "@bitwarden/web-vault/app/models/view/calculated.property";
import {
  AccountLinkedProperties,
  LinkedProperty,
  LinkedView,
} from "@bitwarden/web-vault/app/models/view/linked.property";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { InstitutionAccountView } from "@bitwarden/web-vault/app/models/view/institution/institution-account.view";
import { AccountType } from "@bitwarden/web-vault/app/models/types/account.types";
import { CurrencyType } from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { SyncStatusView } from "@bitwarden/web-vault/app/models/view/sync-status/sync-status.view";

export const isBankOrCredit = (type: AccountType): boolean => {
  return type === "bank" || type === "credit";
};

export const isStockOrOther = (type: AccountType): boolean => {
  return type === "stock" || type === "other";
};

export class AccountView
  extends ModelView<AccountStoreModel>
  implements
    CalculatedProperty<BalanceCalculatedProperties>,
    LinkedProperty<AccountLinkedProperties>
{
  /** Model version in Use **/
  protected version: AccountModelVersion;

  /** Updatable Properties **/
  private _name: string;
  /** Optional user-set nickname for the account (not overwritten by sync) */
  nickname?: string;
  /** Public name accessor. Always returns the original institution/product name. */
  get name(): string {
    return this._name;
  }
  set name(v: string) {
    this._name = v;
  }
  /** Display name for UI: prefer user-set `nickname`, otherwise the original `name`. */
  get displayName(): string {
    return this.nickname?.trim() || this.name;
  }
  /** The original product name as provided by the institution/source. Use this when editing the account. */
  get originalName(): string {
    return this._name;
  }
  type: AccountType;
  currency: CurrencyType;
  origin: Origin;
  // TODO: We should remove c_institution as we shouldn't copy a stale version of the InstitutionView into the AccountView
  c_institution: InstitutionView;
  institutionLink: InstitutionAccountLink;
  rootInstitutionLink: InstitutionAccountLink; // the original link to be saved when an insto is linked
  defaultClassifications: SplitClassificationType[];
  defaultCategories: SplitCategory[];
  // TODO: We should remove c_institutionAccountType as we shouldn't copy a stale version of this data into the AccountView
  c_institutionAccountType: InstitutionAccountView;
  timezone: string;

  /* @deprecated */
  // TODO update this property as providerAccountId as we can have multiple providers
  basiqAccountId: string;
  creditLimit: number;

  /** use this instead of status */
  syncStatusLink: LinkedView[];
  c_syncStatus: SyncStatusView[];
  source: string;

  /** BalanceCalculation **/
  c_balance: number;

  /** **/
  c_maxInterestRate: number = NaN;

  /**  @Deprecated */
  status: string[];
  /**  @Deprecated */
  originalBook: Book;
  /** @deprecated **/
  balance: number;
  /** @deprecated **/
  legacyId: string;

  constructor(model?: AccountStoreModel) {
    super(model);

    if (typeof model === "object") {
      this.source = model.ori;
      /** @Alex I guess we need to remove this balance from the model cuz when it compares/loads it requires it */
      this.balance = model.bal;
      this.creditLimit = model.crLm;
      this.currency = model.curr;
      this.basiqAccountId = model.bsqId;
      this.defaultCategories = SplitCategoryFactory.createFromSplitCategoryObjectArray(
        model.dfltCat,
        true,
      );
      this.defaultClassifications = model.dfltCla;
      this.institutionLink = model.instLnk;
      this.rootInstitutionLink = model.rtInstLnk;
      this._name = model.name;
      this.nickname = (model as any).nick;
      this.origin = model.ori;
      this.type = model.type;
      this.timezone = model.tz;
      this.basiqAccountId = model.bsqId;
      this.syncStatusLink = (model.st ?? []).map((id) => ({ id }));
      this.vaultId = model.vid;

      /** Remove after cleaning up. **/
      this.legacyId = this.id;
    } else {
      this.version = DefaultAccountStoreModel.v;
    }
  }

  toStoreModel(): AccountStoreModel {
    return {
      bsqId: this.basiqAccountId,
      curr: this.currency,
      crLm: this.creditLimit,
      date: "",
      dc: this.dateCreated.toISOString(),
      dfltCat: this.defaultCategories
        ? this.defaultCategories.map((cat) => cat.toSplitCategoryAsObjectType())
        : [],
      dfltCla: this.defaultClassifications,
      dm: new Date().toISOString(),
      id: this.id,
      instLnk: this.institutionLink,
      rtInstLnk: this.rootInstitutionLink,
      name: this._name,
      nick: this.nickname,
      bal: this.balance,
      ori: this.origin,
      type: this.type,
      st: this.syncStatusLink ? this.syncStatusLink.map((link) => link.id) : [],
      tz: this.timezone,
      v: this.version,
      vid: this.vaultId,
      oid: this.organizationId,
    };
  }

  clone(): AccountView {
    const copy = new AccountView(this.toStoreModel());
    copy.balance = this.balance;
    copy.c_balance = this.c_balance;
    copy.c_institution = this.c_institution?.clone();
    copy.c_institutionAccountType = this.c_institutionAccountType;
    return copy;
  }

  /**
   * @deprecated
   */
  legacyConstructor(account: Book, institution: InstitutionView): this {
    this.legacyId = account.id;
    this.originalBook = account;
    this.name = account.name;
    this.type = account.type;
    this.currency = account.currency;
    this.origin = account.origin;
    this.status = []; // TODO when it is implemented update this part as well & it's an array of status ids
    this.institutionLink = account.institutionLink;
    this.c_institution = this.setInstitution(institution);
    this.defaultClassifications = account.defaultClassifications;
    this.defaultCategories = account.defaultCategories.map((oldCat) => {
      return SplitCategoryFactory.createFromSplitCategoryObject({
        ctId: oldCat.category.id,
        wgt: oldCat.weight,
        rdf: oldCat?.roundingDefault ? oldCat.roundingDefault : false,
      });
    });
    this.setInstitutionAccountType();
    this.setSource();
    return this;
  }

  /**
   * @deprecated
   */
  setInstitution(institution: InstitutionView) {
    if (institution) {
      return institution;
    } else {
      return { id: "", name: "unknown" } as unknown as InstitutionView;
    }
  }

  /**
   * @deprecated
   */
  setInstitutionAccountType() {
    if (
      this.c_institution &&
      this.c_institution.availableAccounts?.length &&
      this.institutionLink &&
      this.institutionLink.institutionAccountId
    ) {
      this.c_institutionAccountType = this.c_institution.availableAccounts.find(
        (institutionAccount) => institutionAccount.id === this.institutionLink.institutionAccountId,
      );
    } else {
      this.c_institutionAccountType = { id: "", name: "unknown", interestRates: [] };
    }
  }

  /**
   * @deprecated
   */
  setSource() {
    if (this.origin !== Origin.manual) {
      this.source = "Auto Sync";
    } else {
      this.source = "Manual Import";
    }
  }
}
